import React, { useState, useEffect } from 'react'
import {
  useQueryParam,
  trialEndedParam,
  subscriptionEndedParam,
} from 'use-query-params'
import { ThemeConsumer } from 'styled-components'
import { Flex, Box } from '@rebass/grid'
import Space from '@rebass/space'
import { Link } from 'gatsby'
import Button from './Button'
import Badge from './Badge'
import axios from 'axios'
import styled from 'styled-components'
import Tippy from '@tippyjs/react'
import 'tippy.js/dist/tippy.css'
import 'tippy.js/themes/light.css'

import HelpIcon from '../images/vector/help.svg'
import { getPriceString, getPriceStringSync } from '../utils/currency'
import { trackEvent } from '../utils/analytics'

const BigRadioOption = styled.label`
  padding: 16px;
  border: none;
  border-radius: 8px;
  background-color: ${(props) =>
    props.theme.appearance === 'light' ? '#fafafa' : 'rgba(0, 0, 0, .35)'};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  * {
    display: inline-block;
    vertical-align: middle;
  }

  input[type='radio'] {
    width: 15px;
    height: 15px;
    margin: 0;
    margin-top: 2px;
    flex-shrink: 0;
  }

  &:hover:not(.active) {
    background-color: ${(props) =>
      props.theme.appearance === 'light' ? '#ffffff' : 'rgba(0, 0, 0, .1)'};
    background: ${(props) =>
      props.theme.appearance === 'light' ? '#ffffff' : 'rgba(0, 0, 0, .1)'};
    box-shadow:
      0 0 0 1px rgba(0, 0, 0, 0.06),
      0 1px 1px 0 rgba(0, 0, 0, 0.04),
      0 1px 2px 0 rgba(0, 0, 0, 0.03),
      0 2px 3px 0 rgba(0, 0, 0, 0.03);
  }

  &.active {
    background-color: ${(props) =>
      props.theme.appearance === 'light' ? '#ffffff' : 'rgba(0, 0, 0, .1)'};
    background: ${(props) =>
      props.theme.appearance === 'light' ? '#ffffff' : 'rgba(0, 0, 0, .1)'};
    box-shadow:
      0 0 0 1px rgba(0, 0, 0, 0.06),
      0 1px 1px 0 rgba(0, 0, 0, 0.04),
      0 1px 2px 0 rgba(0, 0, 0, 0.03),
      0 2px 3px 0 rgba(0, 0, 0, 0.03);
  }
`

const BuyLicenseForm = (props) => {
  ///////////////////////////////////////////
  // Check if a visitor is on Windows
  ///////////////////////////////////////////
  function isWindows() {
    if (typeof navigator !== 'undefined') {
      return navigator.platform.indexOf('Win') > -1
    } else {
      return false
    }
  }

  ///////////////////////////////////////////
  // Check when trial ended
  ///////////////////////////////////////////
  const [trialEnded, setTrialEnded] = useQueryParam('te', trialEndedParam)
  const [subscriptionEnded, setSubscriptionEnded] = useQueryParam(
    'se',
    subscriptionEndedParam,
  )

  let daysSinceTrialEnded

  function trialEndedDate(numbers) {
    return parseInt(
      numbers
        .match(/.{1,2}/g)
        .reverse()
        .map((item) => String.fromCharCode(parseInt(item, 16)))
        .join(''),
    )
  }

  useEffect(() => {
    if (
      trialEnded ||
      subscriptionEnded ||
      localStorage.getItem('tempSaleSince')
    ) {
      let trialEndedTimestamp =
        trialEndedDate(
          trialEnded
            ? trialEnded
            : subscriptionEnded
              ? subscriptionEnded
              : localStorage.getItem('tempSaleSince'),
        ) * 1000
      daysSinceTrialEnded = parseInt(
        Math.abs((trialEndedTimestamp - Date.now()) / (1000 * 3600 * 24), 10),
      )

      // Store in localstorage
      if (!localStorage.getItem('tempSaleSince')) {
        localStorage.setItem('tempSaleSince', trialEnded)
      }
    }

    if (daysSinceTrialEnded !== null && daysSinceTrialEnded <= 14) {
      setDiscount(true)
    }

    // Defer the query param cleanup to avoid state update conflicts
    setTimeout(() => {
      setTrialEnded(undefined, 'replace')
    }, 0)
  }, [])

  // Update currency prices based on location
  useEffect(() => {
    const updatePrices = async () => {
      try {
        const newAnnualPrice = await getPriceString('annual')
        const newOncePrice = await getPriceString('once')
        setAnnualPrice(newAnnualPrice)
        setOncePrice(newOncePrice)
        setPricesLoaded(true)
      } catch (error) {
        console.log('Failed to update currency prices:', error)
        // Keep using the sync fallback prices
        setPricesLoaded(true)
      }
    }

    updatePrices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  ///////////////////////////////////////////

  let [email, setEmail] = useState('')
  let [quant, setQuantity] = useState(1)
  let [paymentPreference, setPaymentPreference] = useState('subscribe')
  let [buttonState, setButtonState] = useState('')
  let [annualPrice, setAnnualPrice] = useState(getPriceStringSync('annual'))
  let [oncePrice, setOncePrice] = useState(getPriceStringSync('once'))
  let [pricesLoaded, setPricesLoaded] = useState(false)

  let handleChangeQuantity = (e) => setQuantity(e.target.value)
  let handleChangeEmail = (e) => setEmail(e.target.value)
  let handleChangePaymentPreference = (e) =>
    setPaymentPreference(e.target.value)

  let [errorMessage, setErrorMessage] = useState('')

  let [hasCoupon, setHasCoupon] = useState(false)
  let [coupon, setCoupon] = useState('')

  let handleToggleCoupon = (e) => {
    setHasCoupon(e.target.checked)
    if (!e.target.checked) {
      setCoupon('')
    }
  }
  let handleChangeCoupon = (e) => setCoupon(e.target.value)

  ///////////////////////////////////////////

  let handleSubmit = (event) => {
    setButtonState(true)
    setErrorMessage('')
    trackEvent('buy_click', { plan: paymentPreference, quantity: quant })
    axios
      .post(`/api/custom-checkout`, {
        email: email,
        quantity: quant,
        paymentPreference: paymentPreference,
        coupon: hasCoupon ? coupon.trim() : '',
        trialEnded:
          typeof window !== 'undefined'
            ? localStorage.getItem('tempSaleSince')
            : '',
      })
      .then((res) => {
        trackEvent('checkout_open', {
          plan: paymentPreference,
          quantity: quant,
        })
        window.Paddle.Checkout.open({
          override: res.data,
          successCallback: (data) => {
            trackEvent('purchase', {
              transaction_id: data && data.checkout && data.checkout.id,
              value:
                data &&
                data.checkout &&
                data.checkout.prices &&
                data.checkout.prices.customer &&
                data.checkout.prices.customer.total,
              currency:
                data &&
                data.checkout &&
                data.checkout.prices &&
                data.checkout.prices.customer &&
                data.checkout.prices.customer.currency,
              plan: paymentPreference,
              quantity: quant,
            })
          },
        })
        setButtonState(false)
      })
      .catch(function (error) {
        setButtonState(false)

        const status = error.response && error.response.status
        if (status === 502 && hasCoupon && coupon.trim()) {
          setErrorMessage(
            "We couldn't apply that coupon code. Double-check it, or uncheck the box to continue without it.",
          )
        } else {
          setErrorMessage(
            'An error occured generating your checkout. Please email us at info@geticonjar.com to get this resolved.',
          )
        }
        console.log(error)
      })

    event.preventDefault()
  }

  return (
    <ThemeConsumer>
      {(theme) => (
        <form onSubmit={handleSubmit} id="purchase-form">
          <Box p={4}>
            <h4>
              Get your license{' '}
              {isWindows() && (
                <Badge
                  colorscheme="orange"
                  style={{ lineHeight: '1.65em', borderRadius: '6px' }}
                  px={3}
                  py={2}
                  mt={3}>
                  ⚠️ It looks like you're not running macOS. Please note that
                  IconJar isn't compatible with Windows or Linux.
                </Badge>
              )}
            </h4>

            <Box my={4}>
              <BigRadioOption
                style={{
                  fontWeight: '400',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  opacity: pricesLoaded ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out',
                }}
                className={paymentPreference === 'subscribe' ? 'active' : ''}>
                <span style={{ fontWeight: '500' }}>
                  Annually <br />
                  <span className="small faded" style={{ fontWeight: '400' }}>
                    {annualPrice}/year
                  </span>
                </span>
                <input
                  type="radio"
                  name="paymentPreference"
                  value="subscribe"
                  onChange={handleChangePaymentPreference}
                  checked={
                    paymentPreference === 'subscribe' ? 'checked' : ''
                  }></input>
              </BigRadioOption>
              <BigRadioOption
                style={{
                  fontWeight: '400',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  opacity: pricesLoaded ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out',
                }}
                className={paymentPreference === 'lifetime' ? 'active' : ''}>
                <span style={{ fontWeight: '500' }}>
                  Once{' '}
                  <Tippy
                    theme={theme.appearance === 'light' ? 'light' : ''}
                    content={
                      <p>
                        Pay for IconJar 2 once. Major updates will become
                        available as a paid upgrade.
                      </p>
                    }>
                    <div
                      style={{
                        display: 'inline-block',
                        marginLeft: '4px',
                        marginTop: '-4px',
                      }}
                      className="xs-hide">
                      <HelpIcon
                        style={{
                          width: '16px',
                          fill:
                            theme.appearance === 'light' ? '#31383C' : '#FFF',
                        }}
                      />
                    </div>
                  </Tippy>{' '}
                  <br />
                  <span className="small faded" style={{ fontWeight: '400' }}>
                    {oncePrice}
                  </span>
                </span>
                <input
                  type="radio"
                  name="paymentPreference"
                  value="lifetime"
                  onChange={handleChangePaymentPreference}
                  checked={
                    paymentPreference === 'lifetime' ? 'checked' : ''
                  }></input>
              </BigRadioOption>
            </Box>

            <Flex alignItems="center" flexWrap={['wrap', 'inherit']} mb={4}>
              <Box width={[1, 1 / 4, 1 / 4]} mr={[0, 3]} mb={[3, 0]}>
                <label htmlFor="numberOfLicenses">Quantity</label>
                <input
                  type="number"
                  name="quantity"
                  id="numberOfLicenses"
                  placeholder="1"
                  value={quant}
                  onChange={handleChangeQuantity}
                />
              </Box>
              <Box width={[1, 3 / 4, 3 / 4]}>
                <label htmlFor="emailInput">Email address</label>
                <input
                  type="email"
                  name="email"
                  id="emailInput"
                  placeholder="frederique@example.com"
                  value={email}
                  onChange={handleChangeEmail}
                />
              </Box>
            </Flex>

            <Box mb={3}>
              <label
                htmlFor="hasCoupon"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontWeight: '400',
                }}>
                <input
                  type="checkbox"
                  id="hasCoupon"
                  name="hasCoupon"
                  checked={hasCoupon}
                  onChange={handleToggleCoupon}
                  style={{
                    width: '15px',
                    height: '15px',
                    margin: '0 8px 0 0',
                    flexShrink: 0,
                  }}
                />
                I have a coupon code
              </label>
              {hasCoupon && (
                <Box mt={3}>
                  <label htmlFor="couponInput">Coupon code</label>
                  <input
                    type="text"
                    name="coupon"
                    id="couponInput"
                    placeholder="Enter your coupon code"
                    value={coupon}
                    onChange={handleChangeCoupon}
                  />
                </Box>
              )}
            </Box>

            <p className="small faded" style={{ marginTop: 0 }}>
              Clicking "Buy now" opens our secure checkout, where you can
              optionally add a VAT number. Your invoice is emailed to the
              address above.
            </p>
            {errorMessage && (
              <Box mb={3}>
                <p className="small" style={{ color: '#e5484d', margin: 0 }}>
                  {errorMessage}
                </p>
              </Box>
            )}
            <Button
              colorscheme="green"
              form="purchase-form"
              value="submit"
              role="button"
              showprogress={buttonState}
              disabled={buttonState}
              stretched
              mb={3}>
              Buy now
            </Button>
            <Space mt={3}>
              <p className="small">
                By clicking "Buy now", you accept our{' '}
                <Link to="/terms-of-use">terms of use</Link> and{' '}
                <Link to="/privacy-policy">privacy policy</Link>.
              </p>
            </Space>
          </Box>
        </form>
      )}
    </ThemeConsumer>
  )
}

export default BuyLicenseForm
